import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import {
  CourseManagerGetter,
  CourseManagerType,
} from 'layers/content/v2/Hocs/CourseManagerGetter'
import ContentDataGetter from 'layers/content/v2/Hocs/ContentDataGetter'
import { operations as loadingOperations } from 'store/loadingManager'
import { isK5Dashboard, isMiddleSchoolDashboard } from 'utils/productHelpers'
import Component from './component'
import { determineK8ProgramStatus, K8ProgramStatus } from './states'
import {
  hasTeacherPreferences,
  hasCreatedClass,
  hasCompletedK8Training,
  hasCompletedSomeK8Training,
  programResolver,
  getProgramPreferencesAndTraining,
} from './helpers'

/**
 * Resolves the appropriate entry ID based on the current dashboard type
 */
const resolveEntryId = () => {
  if (isK5Dashboard()) {
    return '22dJQd20K1xZpBKDuJrocQ'
  } else if (isMiddleSchoolDashboard()) {
    return 'xnAu3WNQ6CuEeHDrsiIMh'
  }
  return '22dJQd20K1xZpBKDuJrocQ'
}

/**
 * Container for K8ProgramStatusRouter component
 *
 * Handles business logic for determining program status based on user data.
 * Uses CourseManagerGetter to select appropriate program based on dashboard type.
 */
const K8ProgramStatusRouterContainer = React.forwardRef((props, ref) => {
  const {
    content,
    currentSite,
    isK5Product,
    isMsProduct,
    courseManager,
    instructorData,
    isFetchingInstructorData,
    isLoadingApp,
    setAppIsLoadingState,
    setAppNotLoadingState,
  } = props || {}

  // Extract purpose values from the correct site preferences based on program type
  let purpose = ''
  let purposeIsCompleted = false

  // pretty sure we can just use one of these but that might change in the future
  if (isK5Product) {
    const k5SitePreferences = currentSite?.k5SitePreferences || {}
    purpose = k5SitePreferences?.purpose || ''
    purposeIsCompleted = k5SitePreferences?.purposeIsCompleted || false
  } else if (isMsProduct) {
    const middleSchoolSitePreferences =
      currentSite?.middleSchoolSitePreferences || {}
    purpose = middleSchoolSitePreferences?.purpose || ''
    purposeIsCompleted =
      middleSchoolSitePreferences?.purposeIsCompleted || false
  }

  // Check if any of our required data sources are loading
  const isCourseManagerLoading =
    courseManager?.loading || courseManager?.isUserContextLoading
  const isCurrentSiteLoading = !currentSite
  const isInstructorDataLoading = isFetchingInstructorData || !instructorData
  const isContentLoading = content?.loading

  const isLoading =
    isCourseManagerLoading ||
    isCurrentSiteLoading ||
    isInstructorDataLoading ||
    isContentLoading

  useEffect(() => {
    if (isLoading && !isLoadingApp) {
      setAppIsLoadingState('Loading program data...')
    } else if (!isLoading && isLoadingApp) {
      setAppNotLoadingState()
    }

    return () => {
      if (isLoadingApp) {
        setAppNotLoadingState()
      }
    }
  }, [isLoading, isLoadingApp, setAppIsLoadingState, setAppNotLoadingState])

  if (isLoading) {
    return (
      <Component
        {...props}
        content={content}
        courseManager={courseManager}
        isLoading={true}
        programStatus={null}
        ref={ref}
        statusEnum={K8ProgramStatus}
      />
    )
  }

  const teacherPreferencesSet = hasTeacherPreferences(
    currentSite,
    isK5Product,
    isMsProduct,
  )

  const trainingCompleted = hasCompletedK8Training(
    courseManager,
    instructorData,
    isK5Product,
    isMsProduct,
  )

  const someTrainingCompleted = hasCompletedSomeK8Training(
    courseManager,
    instructorData,
    isK5Product,
    isMsProduct,
  )

  const classCreated = hasCreatedClass(
    instructorData,
    currentSite,
    isK5Product,
    isMsProduct,
  )

  // Determine which preferences to check based on product type and extract requiredTraining
  const {
    programPreferences,
    requiredTraining,
  } = getProgramPreferencesAndTraining(currentSite, isK5Product, isMsProduct)

  const currentStatus = determineK8ProgramStatus({
    hasTeacherPreferences: teacherPreferencesSet,
    hasCompletedTraining: trainingCompleted,
    hasCreatedClass: classCreated,
    someTrainingCompleted: someTrainingCompleted,
    requiredTraining: requiredTraining,
  })

  return (
    <Component
      {...props}
      content={content}
      courseManager={courseManager}
      instructorData={instructorData}
      isLoading={false}
      programPreferences={programPreferences}
      programStatus={currentStatus}
      purpose={purpose}
      purposeIsCompleted={purposeIsCompleted}
      ref={ref}
      someTrainingCompleted={someTrainingCompleted}
      statusEnum={K8ProgramStatus}
      trainingCompleted={trainingCompleted}
    />
  )
})

K8ProgramStatusRouterContainer.propTypes = {
  content: PropTypes.shape({
    data: PropTypes.object,
    error: PropTypes.object,
    loading: PropTypes.bool,
    fetch: PropTypes.func,
  }),
  courseManager: CourseManagerType,
  currentSite: PropTypes.shape({
    k5EducatorPreferences: PropTypes.object,
    middleSchoolEducatorPreferences: PropTypes.object,
  }),
  instructorData: PropTypes.object,
  isFetchingInstructorData: PropTypes.bool,
  isK5Product: PropTypes.bool,
  isLoadingApp: PropTypes.bool,
  isMsProduct: PropTypes.bool,
  match: PropTypes.shape({
    url: PropTypes.string,
  }),
  setAppIsLoadingState: PropTypes.func,
  setAppNotLoadingState: PropTypes.func,
}

K8ProgramStatusRouterContainer.displayName = 'K8ProgramStatusRouterContainer'

const mapStateToProps = state => ({
  instructorData: state?.lmsManager?.instructorData,
  isFetchingInstructorData: state?.lmsManager?.isFetchingOrCreatingProgramFlex,
  isLoadingApp: state?.loadingManager?.isLoading,
})

const mapDispatchToProps = {
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
  setAppNotLoadingState: loadingOperations.setNotLoadingState,
}

export default ContentDataGetter(
  withSiteContext(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(
      CourseManagerGetter(K8ProgramStatusRouterContainer, {
        programResolver,
      }),
    ),
  ),
  {
    dynamicEntryId: resolveEntryId,
    queryType: 'dashboardProgram',
  },
)
